/**
 * TopNavBar — Global navigation bar shown across all views.
 *
 * Provides: Home button, current game name, game-specific menu,
 * fullscreen toggle, auth button.
 * Sticky at the top of the viewport. Collapses gracefully on mobile.
 */

import React, { useState, useRef, useEffect } from 'react';
import { FullScreenToggle } from './FullScreenToggle';
import { AuthButton } from './AuthButton';
import './TopNavBar.css';

export interface NavMenuItem {
  id: string;
  label: string;
  icon?: string;
  onClick: () => void;
  disabled?: boolean;
  divider?: boolean;
  danger?: boolean;
}

interface TopNavBarProps {
  /** Current view label (e.g., "Ludo", "Chess", or null for hub) */
  currentGame?: string | null;
  /** Navigate back to GameHub */
  onHome: () => void;
  /** Auth state */
  isAuthenticated: boolean;
  user?: any;
  onAuthClick: () => void;
  /** Whether we're on the hub (hides back arrow) */
  isHub?: boolean;
  /** Game-specific menu items */
  menuItems?: NavMenuItem[];
}

export const TopNavBar: React.FC<TopNavBarProps> = ({
  currentGame,
  onHome,
  isAuthenticated,
  user,
  onAuthClick,
  isHub = false,
  menuItems,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  // Close menu on Escape
  useEffect(() => {
    if (!menuOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [menuOpen]);

  const hasMenu = menuItems && menuItems.length > 0;

  return (
    <nav className="top-nav" role="navigation" aria-label="Main navigation">
      <div className="top-nav__left">
        {!isHub && (
          <button
            className="top-nav__home-btn"
            onClick={onHome}
            aria-label="Back to Game Hub"
            title="Game Hub"
          >
            ← Home
          </button>
        )}
        <button
          className="top-nav__logo"
          onClick={onHome}
          aria-label="PlayHub Games home"
        >
          🎮 PlayHub
        </button>
        {currentGame && (
          <span className="top-nav__game-name" aria-current="page">
            / {currentGame}
          </span>
        )}
      </div>

      <div className="top-nav__right">
        {/* Game-specific menu */}
        {hasMenu && (
          <div className="top-nav__menu-container" ref={menuRef}>
            <button
              className={`top-nav__menu-btn ${menuOpen ? 'top-nav__menu-btn--open' : ''}`}
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Game menu"
              aria-expanded={menuOpen}
              aria-haspopup="true"
              title="Menu"
            >
              ☰
            </button>

            {menuOpen && (
              <div className="top-nav__dropdown" role="menu" aria-label="Game options">
                {menuItems!.map((item, idx) => (
                  <React.Fragment key={item.id}>
                    {item.divider && idx > 0 && <div className="top-nav__dropdown-divider" role="separator" />}
                    <button
                      className={`top-nav__dropdown-item ${item.danger ? 'top-nav__dropdown-item--danger' : ''} ${item.disabled ? 'top-nav__dropdown-item--disabled' : ''}`}
                      onClick={() => {
                        if (!item.disabled) {
                          item.onClick();
                          setMenuOpen(false);
                        }
                      }}
                      disabled={item.disabled}
                      role="menuitem"
                    >
                      {item.icon && <span className="top-nav__dropdown-icon">{item.icon}</span>}
                      <span>{item.label}</span>
                    </button>
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
        )}

        <FullScreenToggle />
        <AuthButton
          isAuthenticated={isAuthenticated}
          user={user}
          onClick={onAuthClick}
        />
      </div>
    </nav>
  );
};

export default TopNavBar;
